<script setup lang="ts">
import {
    getSecupayTransactionStatus,
    generateNewPaymentLink as generateNewPaymentLinkApi,
} from '../../../utilities/api/secupay'
import { useInvestmentProcessDataState } from '../../../utilities/investment-process/useInvestmentProcess'
import { getOrdersLink } from '../../../utilities/useLinks'
import BaseButtonMuted from '../../base/BaseButtonMuted.vue'
import BaseButtonPrimary from '../../base/BaseButtonPrimary.vue'
import CheckIcon from '../../base/icons/CheckIcon.vue'
import CrossIcon from '../../base/icons/CrossIcon.vue'
import Modal from '../../dialogs/Modal.vue'
import { storeToRefs } from 'pinia'
import { ref, watch } from 'vue'
import { defineEmits } from 'vue'

const emit = defineEmits(['paymentFinished'])

const navigateToPayment = () => {
    window.open(paymentData?.value?.payment_links.general, '_blank')
    paymentStarted.value = true
    modalOpen.value = true
}

const investmentProcessDataState = useInvestmentProcessDataState()
const { setPaymentData } = investmentProcessDataState
const { paymentData, createdOrder } = storeToRefs(investmentProcessDataState)

const paymentStarted = ref<boolean>(false)
const paymentFailed = ref<boolean>(false)
const paymentSuccessful = ref<boolean>(false)
const prepaidPaymentSuccessful = ref<boolean>(false)
const modalOpen = ref<boolean>(false)
const interval = ref<number | null>(null)

watch(
    () => paymentStarted.value,
    () => {
        if (paymentStarted.value) {
            interval.value = window.setInterval(async () => {
                const response = await getSecupayTransactionStatus(paymentData.value?.payment_id as string)
                if (response.status === 200) {
                    // handle success for credit card payment
                    if (response.data.details.status_simple === 1 || response.data.details.status_simple === 9) {
                        paymentSuccessful.value = true
                        emit('paymentFinished')
                        if (interval.value) {
                            clearInterval(interval.value)
                            interval.value = null
                        }
                    }
                    // handle failed states
                    else if (
                        response.data.details.status_simple === 3 ||
                        response.data.details.status_simple === 5 ||
                        response.data.details.status_simple === 12
                    ) {
                        paymentFailed.value = true
                        paymentStarted.value = false
                        emit('paymentFinished')
                        if (interval.value) {
                            clearInterval(interval.value)
                            interval.value = null
                        }
                    }
                    // handle pending state(placeholder)
                    else if (response.data.details.status_simple === 99) {
                        return
                    }
                    // handle success for prepaid payment(pending for payment)
                    else if (response.data.details.status_simple === 10 && response.data.payment_method === 'prepaid') {
                        paymentSuccessful.value = true
                        prepaidPaymentSuccessful.value = true
                        emit('paymentFinished')
                        if (interval.value) {
                            clearInterval(interval.value)
                            interval.value = null
                        }
                    } else {
                        paymentSuccessful.value = false
                        if (interval.value) {
                            clearInterval(interval.value)
                            interval.value = null
                        }
                    }
                }
            }, 5000)
        }
    }
)

const generateNewPaymentLink = async () => {
    const response = await generateNewPaymentLinkApi(createdOrder.value?.order_uid as string)
    if (response.status === 200) {
        setPaymentData(response.data.payment_data)
        window.open(response.data.payment_data.payment_links.general, '_blank')
        paymentFailed.value = false
        paymentSuccessful.value = false
        paymentStarted.value = true
        modalOpen.value = true
    }
}
</script>

<template>
    <div class="h-auto w-full space-y-4 bg-white">
        <Modal v-model="modalOpen" :show-close="true" size="sm" :close-on-outside-click="false">
            <div class="flex h-auto w-full flex-col items-center justify-center space-y-6">
                <p
                    v-if="!paymentSuccessful && !paymentFailed"
                    class="text-center text-lg font-bold text-gray-800"
                    v-html="$t('investment_process.payment_processing')"></p>
                <p v-if="paymentFailed && !paymentSuccessful" class="text-center text-lg font-bold text-red-500">
                    {{ $t('investment_process.payment_failed') }}
                </p>
                <p
                    v-if="paymentSuccessful && !prepaidPaymentSuccessful"
                    class="text-center text-lg font-bold text-gray-800">
                    {{ $t('investment_process.payment_successful') }}
                </p>
                <p
                    v-if="prepaidPaymentSuccessful && paymentSuccessful"
                    class="text-center text-lg font-bold text-gray-800">
                    {{ $t('investment_process.prepaid_payment_successful') }}
                </p>
                <span v-if="!paymentSuccessful && !paymentFailed" class="loader"></span>
                <CheckIcon v-if="paymentSuccessful" class="h-20 w-20" />
                <CrossIcon v-if="paymentFailed" class="h-16 w-16" />
                <div class="flex w-full flex-col items-center justify-center space-y-2">
                    <BaseButtonPrimary
                        v-if="paymentFailed && !paymentSuccessful"
                        class="w-full"
                        @click="generateNewPaymentLink"
                        >{{ $t('investment_process.try_again') }}</BaseButtonPrimary
                    >
                    <BaseButtonPrimary v-if="paymentSuccessful" class="w-full" @click="modalOpen = false">{{
                        $t('investment_process.close')
                    }}</BaseButtonPrimary
                    ><BaseButtonMuted v-if="paymentFailed" class="w-full" @click="modalOpen = false">{{
                        $t('investment_process.cancel')
                    }}</BaseButtonMuted>
                </div>
            </div>
        </Modal>
        <BaseButtonPrimary v-if="!paymentSuccessful" class="w-full" @click="navigateToPayment">{{
            $t('investment_process.pay_now')
        }}</BaseButtonPrimary>
    </div>
</template>

<style scoped>
.loader {
    width: 48px;
    height: 48px;
    border-radius: 50%;
    display: inline-block;
    position: relative;
    border: 3px solid;
    border-color: #004511 #004511 transparent transparent;
    box-sizing: border-box;
    animation: rotation 1s linear infinite;
}
.loader::after,
.loader::before {
    content: '';
    box-sizing: border-box;
    position: absolute;
    left: 0;
    right: 0;
    top: 0;
    bottom: 0;
    margin: auto;
    border: 3px solid;
    border-color: transparent transparent #050432 #050432;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    box-sizing: border-box;
    animation: rotationBack 0.5s linear infinite;
    transform-origin: center center;
}
.loader::before {
    width: 32px;
    height: 32px;
    border-color: #fff #fff transparent transparent;
    animation: rotation 1.5s linear infinite;
}

@keyframes rotation {
    0% {
        transform: rotate(0deg);
    }
    100% {
        transform: rotate(360deg);
    }
}
@keyframes rotationBack {
    0% {
        transform: rotate(0deg);
    }
    100% {
        transform: rotate(-360deg);
    }
}
</style>
